<template>
  <div class="mx-auto max-w-4xl space-y-6 p-6">
    <h1 class="text-3xl font-bold text-theme-fg">
      WebRTC File Transfer
    </h1>

    <Card>
      <CardHeader>
        <CardTitle>Peer-to-peer file transfer</CardTitle>
        <CardDescription>
          <StatusIndicator :status="indicatorStatus" :pulse="status === 'connecting'" :label="statusText" />
        </CardDescription>
      </CardHeader>

      <CardContent class="space-y-6">
        <!-- Role selection -->
        <div v-if="role === null" class="flex flex-col gap-4 sm:flex-row">
          <Button class="flex-1" :loading="isBusy" :disabled="isBusy" @click="startAsHost">
            {{ isBusy ? 'Preparing offer…' : 'Start a transfer session' }}
          </Button>
          <Button class="flex-1" variant="outline" :disabled="isBusy" @click="join">
            Join a session
          </Button>
        </div>

        <!-- Host: offer + scan answer -->
        <div v-if="role === 'host' && status !== 'connected'" class="grid gap-6 md:grid-cols-2">
          <section class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              1. Scan this QR with the other device's camera app
            </h3>
            <!-- Opened by a camera app, which cannot join parts: never split. -->
            <QrFrames v-if="offer" :value="offer" level="L" :allow-fragments="false" alt="Transfer offer QR code" @error="onQrError" />
            <p class="text-center text-xs text-theme-neutral-6">
              It opens this page there and answers on its own.
            </p>
            <div class="flex justify-center">
              <ClipboardButton :text="offer" label="Copy link" success-message="Link copied" />
            </div>
          </section>
          <section class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              2. Scan the answer QR shown on the other device
            </h3>
            <template v-if="!answerApplied">
              <QrScanner
                v-if="showScanner"
                :continuous="false"
                :validate="isAnswer"
                :show-error="false"
                :width="288"
                :height="288"
                video-label="Camera for scanning the answer QR"
                @detected="(p) => { showScanner = false; answerInput = p }"
                @rejected="error = 'That QR is not an answer. Scan the answer shown on the other device.'"
                @error="onCameraError"
              />
              <Button v-else variant="outline" size="sm" @click="showScanner = true">
                Open camera
              </Button>
            </template>
            <p v-else class="text-sm text-theme-ok">
              Answer received — connecting…
            </p>
          </section>
          <details class="md:col-span-2">
            <summary class="cursor-pointer text-sm text-theme-neutral-6">
              No camera? Paste the answer
            </summary>
            <Textarea v-model="answerInput" :rows="3" class="mt-2 font-mono text-xs" placeholder="a1~…" />
          </details>
        </div>

        <!-- Guest: scan offer, show answer -->
        <div v-if="role === 'guest' && status !== 'connected'" class="space-y-4">
          <section v-if="!answer" class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              1. Scan the offer QR on the device that started the session
            </h3>
            <QrScanner
              v-if="showScanner"
              :continuous="false"
              :validate="isOffer"
              :show-error="false"
              :width="288"
              :height="288"
              video-label="Camera for scanning the offer QR"
              @detected="(p) => { showScanner = false; offerInput = p }"
              @rejected="error = 'That QR is not a session offer. Scan the QR on the other device.'"
              @error="onCameraError"
            />
            <Button v-else variant="outline" size="sm" @click="showScanner = true">
              Open camera
            </Button>
            <details>
              <summary class="cursor-pointer text-sm text-theme-neutral-6">
                No camera? Paste the link
              </summary>
              <Textarea v-model="offerInput" :rows="3" class="mt-2 font-mono text-xs" placeholder="https://…/file-transfer#o=…" />
            </details>
          </section>
          <section v-else class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              2. Show this answer QR to the device that started the session
            </h3>
            <!-- Read by this app's scanner, which joins parts. -->
            <QrFrames :value="answer" level="L" alt="Transfer answer QR code" @error="onQrError" />
            <div class="flex justify-center">
              <ClipboardButton :text="answer" label="Copy answer" success-message="Answer copied" />
            </div>
          </section>
        </div>

        <!-- Connected: send + receive -->
        <template v-if="status === 'connected'">
          <!-- On-The-Fly Mode & Direct-To-Disk Indicator -->
          <div class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-theme-border bg-theme-surface-2 p-3 text-xs">
            <div class="flex items-center gap-2">
              <span class="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span class="font-medium text-theme-fg">On-The-Fly WebRTC Aktif</span>
              <span class="text-theme-neutral-6">— Parça parça anlık akış (Bellek şişmesi yok)</span>
            </div>
            <label v-if="hasDirectDiskSupport" class="flex items-center gap-1.5 cursor-pointer text-theme-fg select-none">
              <input type="checkbox" v-model="directDiskEnabled" class="rounded border-theme-border text-theme-brand focus:ring-theme-brand" />
              <span class="font-medium">Direct-to-Disk (0 MB RAM)</span>
            </label>
          </div>

          <!-- Real-Time Telemetry: Active Upload -->
          <div v-if="activeUploadStats" class="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 min-w-0">
                <span class="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">GÖNDERİLİYOR</span>
                <span class="text-sm font-medium text-theme-fg truncate">{{ activeUploadStats.fileName }}</span>
              </div>
              <span class="font-mono text-xs font-semibold text-emerald-600 dark:text-emerald-400 shrink-0">{{ activeUploadStats.speedFormatted }}</span>
            </div>
            <div class="w-full bg-theme-surface-3 rounded-full h-2 overflow-hidden">
              <div class="bg-emerald-500 h-2 rounded-full transition-all duration-200" :style="{ width: `${activeUploadStats.percent}%` }"></div>
            </div>
            <div class="flex items-center justify-between text-xs text-theme-neutral-6">
              <span>Parça {{ activeUploadStats.currentChunk }} / {{ activeUploadStats.totalChunks }} ({{ formatFileSize(activeUploadStats.bytesTransferred) }} / {{ formatFileSize(activeUploadStats.fileSize) }})</span>
              <span>Kalan: {{ activeUploadStats.etaFormatted }}</span>
            </div>
          </div>

          <!-- Real-Time Telemetry: Active Download -->
          <div v-if="activeDownloadStats" class="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-2">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 min-w-0">
                <span class="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  {{ activeDownloadStats.directDisk ? 'DİREKT DİSKE AKITILIYOR' : 'ALINIYOR' }}
                </span>
                <span class="text-sm font-medium text-theme-fg truncate">{{ activeDownloadStats.fileName }}</span>
              </div>
              <span class="font-mono text-xs font-semibold text-blue-600 dark:text-blue-400 shrink-0">{{ activeDownloadStats.speedFormatted }}</span>
            </div>
            <div class="w-full bg-theme-surface-3 rounded-full h-2 overflow-hidden">
              <div class="bg-blue-500 h-2 rounded-full transition-all duration-200" :style="{ width: `${activeDownloadStats.percent}%` }"></div>
            </div>
            <div class="flex items-center justify-between text-xs text-theme-neutral-6">
              <span>Parça {{ activeDownloadStats.currentChunk }} / {{ activeDownloadStats.totalChunks }} ({{ formatFileSize(activeDownloadStats.bytesTransferred) }} / {{ formatFileSize(activeDownloadStats.fileSize) }})</span>
              <span>Kalan: {{ activeDownloadStats.etaFormatted }}</span>
            </div>
          </div>

          <section class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              Send files
            </h3>
            <!-- A row turns "success" only after the receiver confirmed it
                 rebuilt the file and the checksum matched (see sendFile). -->
            <FileUpload
              multiple
              :max-size="MAX_FILE_SIZE"
              :upload-fn="sendFile"
              @error="(message) => (error = message)"
            />
            <p class="text-xs text-theme-neutral-6">
              Up to {{ formatFileSize(MAX_FILE_SIZE) }} per file. On-the-fly streaming reads chunks on demand without RAM bottleneck.
            </p>
          </section>

          <section class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              Received files
            </h3>
            <p v-if="incomingNames.length" class="text-sm text-theme-neutral-6">
              Receiving: {{ incomingNames.join(', ') }}
            </p>
            <p v-if="receivedFiles.length === 0 && !incomingNames.length" class="text-sm text-theme-neutral-6">
              Nothing received yet.
            </p>
            <ul v-else class="space-y-2">
              <li
                v-for="file in receivedFiles"
                :key="file.url || file.name"
                class="flex items-center justify-between gap-3 rounded-lg border border-theme-border p-3"
              >
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium text-theme-fg">
                    {{ file.name }}
                  </p>
                  <p class="text-xs text-theme-neutral-6">
                    {{ formatFileSize(file.size) }} · 
                    <span v-if="file.directDisk" class="text-emerald-600 font-medium">Doğrudan Diske Yazıldı (0 RAM)</span>
                    <span v-else>{{ file.verified ? 'SHA-256 verified' : 'size checked' }}</span>
                  </p>
                </div>
                <Button v-if="file.url" as-child size="sm" variant="outline">
                  <a :href="file.url" :download="file.name">Download</a>
                </Button>
                <span v-else-if="file.directDisk" class="text-xs font-medium text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded">
                  Diskte Hazır
                </span>
              </li>
            </ul>
          </section>
        </template>

        <Alert v-if="error" variant="destructive">
          <AlertDescription>{{ error }}</AlertDescription>
        </Alert>
      </CardContent>

      <CardFooter v-if="role !== null">
        <Button variant="destructive" @click="disconnect">
          {{ status === 'connected' ? 'Disconnect' : 'Cancel' }}
        </Button>
      </CardFooter>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>How it works</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <ol class="list-inside list-decimal space-y-2 text-theme-fg">
          <li>Device A: "Start a transfer session" — shows an offer QR and turns its camera on.</li>
          <li>Device B: "Join a session" — scans A's QR and shows an answer QR.</li>
          <li>Device A scans B's answer. When the data channel opens, both sides can send files.</li>
        </ol>
        <Alert variant="info">
          <AlertTitle>Privacy</AlertTitle>
          <AlertDescription>
            Files travel directly between the two browsers over a DTLS-encrypted data channel; nothing is uploaded to a
            server. The offer and answer contain your network candidates (IP addresses). No TURN relay is configured yet,
            so networks that block direct connections will not connect.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { QrScannerError } from '@elveris/ui/qr-scanner'
import { Alert, AlertDescription, AlertTitle } from '@elveris/ui/alert'
import { Button } from '@elveris/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@elveris/ui/card'
import { ClipboardButton } from '@elveris/ui/clipboard-button'
import { FileUpload } from '@elveris/ui/file-upload'
import { QrFrames } from '@elveris/ui/qr-code'
import { QrScanner } from '@elveris/ui/qr-scanner'
import { StatusIndicator } from '@elveris/ui/status-indicator'
import { Textarea } from '@elveris/ui/textarea'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { webrtcManager } from '../utils/webrtc-native'
import { answerJsonFrom, offerJsonFrom, offerLink, offerTokenFromHash, tokenFromManagerJson } from '../utils/sdp-compact'
import {
  OnTheFlyFileSender,
  OnTheFlyFileReceiver,
  isDirectDiskSupported,
  formatFileSize,
  type LiveTransferStats,
} from '../utils/file-stream-transfer'

// Rebuilt on @elveris/ui (2026-09-30). Upgraded with On-The-Fly WebRTC Slicing (2026-10-09):
// - Direct-to-Disk Streaming (0 MB RAM via File System Access API)
// - On-the-fly Slicing: file.slice() chunk by chunk, zero in-memory buffer bloat
// - Raw Binary DataChannel Framing: Bypasses 33% Base64 and JSON stringification overhead
// - Live Real-Time Telemetry: Instantaneous throughput (MB/s), ETA countdown, and chunk gauges

interface ReceivedFile {
  name: string
  size: number
  url?: string
  verified: boolean
  directDisk?: boolean
}

interface Incoming {
  name: string
  size: number
  mimeType: string
  totalChunks: number
  sha256: string | null
  chunks: (string | null)[]
  received: number
}

type Status = 'disconnected' | 'connecting' | 'connected' | 'error'

// Adaptive 64-256 KiB chunks in binary mode. Max file size lifted to 2 GiB.
const CHUNK_SIZE = 48 * 1024
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024
const canHash = typeof crypto !== 'undefined' && Boolean(crypto.subtle)

const role = ref<'host' | 'guest' | null>(null)
const connectionId = ref<string | null>(null)
const status = ref<Status>('disconnected')
const isBusy = ref(false)
const error = ref('')
const showScanner = ref(false)

const offer = ref('')
const answerInput = ref('')
const answerApplied = ref(false)
const offerInput = ref('')
const answer = ref('')

const hasDirectDiskSupport = isDirectDiskSupported()
const directDiskEnabled = ref(hasDirectDiskSupport)
const activeUploadStats = ref<LiveTransferStats | null>(null)
const activeDownloadStats = ref<LiveTransferStats | null>(null)
const activeSender = ref<OnTheFlyFileSender | null>(null)
const receiverInstance = ref<OnTheFlyFileReceiver | null>(null)

const receivedFiles = ref<ReceivedFile[]>([])
const incoming = new Map<string, Incoming>()
const incomingNames = ref<string[]>([])
const pendingAcks = new Map<string, { resolve: () => void, reject: (e: Error) => void, timer: ReturnType<typeof setTimeout> }>()

// Transfer protocol messages, as sent by send() below.
type TransferMessage =
  | { kind: 'file-meta', id: string, name: string, size: number, mimeType: string, totalChunks: number, sha256?: string | null }
  | { kind: 'file-chunk', id: string, index: number, data: string }
  | { kind: 'file-complete', id: string }
  | { kind: 'file-ack', id: string, ok: boolean, reason?: string }

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : '') || String(e)

// Data-channel messages are JSON; null when a message is not.
function parse(text: string): TransferMessage | null {
  try {
    return JSON.parse(text.trim())
  }
  catch {
    return null
  }
}

const PAGE_PATH = '/file-transfer'
const isAnswer = (t: string) => answerJsonFrom(t) !== null
const isOffer = (t: string) => offerJsonFrom(t, PAGE_PATH) !== null

async function startAsHost() {
  isBusy.value = true
  error.value = ''
  try {
    const { id, qrData } = await webrtcManager.createHostConnection('elveris-file-transfer', 'file transfer')
    role.value = 'host'
    connectionId.value = id
    offer.value = offerLink(tokenFromManagerJson(qrData, 'offer'), PAGE_PATH)
    status.value = 'connecting'
    showScanner.value = true
  }
  catch (e) {
    error.value = `Could not create an offer: ${errorMessage(e)}`
  }
  finally {
    isBusy.value = false
  }
}

function join() {
  role.value = 'guest'
  error.value = ''
  showScanner.value = true
}

watch(answerInput, async (text) => {
  if (!isAnswer(text) || answerApplied.value || !connectionId.value) return
  try {
    await webrtcManager.completeConnection(connectionId.value, answerJsonFrom(text)!)
    answerApplied.value = true
  }
  catch (e) {
    error.value = `The answer could not be applied: ${errorMessage(e)}`
  }
})

watch(offerInput, async (text) => {
  if (!isOffer(text) || answer.value) return
  try {
    const { id, answerData } = await webrtcManager.acceptConnection(offerJsonFrom(text, PAGE_PATH)!)
    connectionId.value = id
    answer.value = tokenFromManagerJson(answerData, 'answer')
    status.value = 'connecting'
  }
  catch (e) {
    error.value = `The offer could not be accepted: ${errorMessage(e)}`
  }
})

// ── Transfer protocol ───────────────────────────────────────────────────────

function send(payload: unknown) {
  if (!connectionId.value || !webrtcManager.sendMessage(connectionId.value, JSON.stringify(payload))) {
    throw new Error('The data channel is not open')
  }
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(binary)
}

function fromBase64(text: string): Uint8Array {
  const binary = atob(text)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

async function sha256Hex(data: ArrayBuffer): Promise<string | null> {
  if (!canHash) return null
  const digest = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('')
}

/** Kit FileUpload `uploadFn`: On-the-fly streaming directly from disk slice */
async function sendFile(file: File, onProgress: (percent: number) => void): Promise<void> {
  const id = connectionId.value
  if (!id) throw new Error('Not connected')

  const sender = new OnTheFlyFileSender(id, webrtcManager)
  activeSender.value = sender

  try {
    await sender.send(file, (stats) => {
      activeUploadStats.value = stats
      onProgress(Math.min(99, Math.round(stats.percent)))
    })
    onProgress(100)
  }
  finally {
    setTimeout(() => {
      if (activeUploadStats.value?.isComplete) {
        activeUploadStats.value = null
      }
    }, 4000)
    activeSender.value = null
  }
}

async function finishIncoming(transferId: string) {
  const t = incoming.get(transferId)
  if (!t) return
  incoming.delete(transferId)
  incomingNames.value = [...incoming.values()].map(x => x.name)

  const reply = (ok: boolean, reason?: string) => {
    try {
      send({ kind: 'file-ack', id: transferId, ok, reason })
    }
    catch {
      // Channel gone: the sender times out and reports the failure itself.
    }
  }

  if (t.chunks.includes(null)) {
    reply(false, `missing ${t.chunks.filter(c => c === null).length} of ${t.totalChunks} chunks`)
    error.value = `Receiving ${t.name} failed: chunks were missing.`
    return
  }
  const parts = t.chunks.map(c => fromBase64(c as string))
  const total = parts.reduce((n, p) => n + p.length, 0)
  const combined = new Uint8Array(total)
  let offset = 0
  for (const p of parts) {
    combined.set(p, offset)
    offset += p.length
  }
  if (total !== t.size) {
    reply(false, `size ${total} != ${t.size}`)
    error.value = `Receiving ${t.name} failed: size mismatch.`
    return
  }
  const hash = t.sha256 ? await sha256Hex(combined.buffer) : null
  if (t.sha256 && hash !== null && hash !== t.sha256) {
    reply(false, 'sha256 mismatch')
    error.value = `Receiving ${t.name} failed: checksum mismatch.`
    return
  }
  const url = URL.createObjectURL(new Blob([combined], { type: t.mimeType || 'application/octet-stream' }))
  receivedFiles.value.push({ name: t.name, size: total, url, verified: Boolean(t.sha256 && hash) })
  reply(true)
}

function onBinaryMessage(event: Event) {
  const { connectionId: cid, data } = (event as CustomEvent).detail ?? {}
  if (cid !== connectionId.value || !data) return

  if (!receiverInstance.value) {
    receiverInstance.value = new OnTheFlyFileReceiver(connectionId.value!, webrtcManager, {
      enableDirectDisk: directDiskEnabled.value,
    })
  }

  void receiverInstance.value.handleBinaryChunk(data, (stats) => {
    activeDownloadStats.value = stats
  })
}

function onMessage(event: Event) {
  const { connectionId: cid, message } = (event as CustomEvent).detail ?? {}
  if (cid !== connectionId.value) return
  const m = parse(String(message))
  if (!m || typeof m !== 'object') return
  switch (m.kind) {
    case 'file-meta':
      if ((m as any).binaryMode) {
        if (!receiverInstance.value) {
          receiverInstance.value = new OnTheFlyFileReceiver(connectionId.value!, webrtcManager, {
            enableDirectDisk: directDiskEnabled.value,
          })
        }
        receiverInstance.value.setDirectDiskEnabled(directDiskEnabled.value)
        void receiverInstance.value.handleMetadata(m as any, async (fileName) => {
          if (typeof window !== 'undefined' && 'showSaveFilePicker' in window) {
            try {
              const picker = (window as any).showSaveFilePicker
              const handle = await picker({ suggestedName: fileName })
              return await handle.createWritable()
            }
            catch (err) {
              console.warn('Direct-to-Disk prompt cancelled; using in-memory binary stream', err)
              return null
            }
          }
          return null
        })
        incomingNames.value = [m.name]
      }
      else {
        incoming.set(m.id, { name: m.name, size: m.size, mimeType: m.mimeType, totalChunks: m.totalChunks, sha256: m.sha256 ?? null, chunks: Array.from({ length: m.totalChunks }, () => null), received: 0 })
        incomingNames.value = [...incoming.values()].map(x => x.name)
      }
      break
    case 'file-chunk': {
      const t = incoming.get(m.id)
      if (t && t.chunks[m.index] === null) {
        t.chunks[m.index] = m.data
        t.received += 1
      }
      break
    }
    case 'file-complete':
      if (receiverInstance.value) {
        void (async () => {
          try {
            const res = await receiverInstance.value!.finalizeTransfer(m.id)
            receivedFiles.value.push({
              name: res.name,
              size: res.size,
              url: res.url,
              verified: true,
              directDisk: res.directDisk,
            })
            incomingNames.value = []
            setTimeout(() => {
              activeDownloadStats.value = null
            }, 3000)
          }
          catch (e) {
            error.value = `Dosya tamamlama hatası: ${errorMessage(e)}`
          }
        })()
      }
      else {
        void finishIncoming(m.id)
      }
      break
    case 'file-ack': {
      const pending = pendingAcks.get(m.id)
      if (!pending) break
      clearTimeout(pending.timer)
      pendingAcks.delete(m.id)
      if (m.ok) pending.resolve()
      else pending.reject(new Error(`Receiver rejected the file: ${m.reason || 'unknown reason'}`))
      break
    }
  }
}

function onState(event: Event) {
  const { connectionId: cid, state } = (event as CustomEvent).detail ?? {}
  if (cid !== connectionId.value) return
  if (state === 'open') {
    status.value = 'connected'
    showScanner.value = false
    error.value = ''
  }
  else if (state === 'closed' || state === 'error') {
    status.value = state === 'error' ? 'error' : 'disconnected'
    for (const [tid, p] of pendingAcks) {
      clearTimeout(p.timer)
      p.reject(new Error('The data channel closed before the receiver confirmed'))
      pendingAcks.delete(tid)
    }
  }
}

function disconnect() {
  if (connectionId.value) webrtcManager.closeConnection(connectionId.value)
  connectionId.value = null
  role.value = null
  status.value = 'disconnected'
  showScanner.value = false
  offer.value = answer.value = answerInput.value = offerInput.value = ''
  answerApplied.value = false
  incoming.clear()
  incomingNames.value = []
  activeUploadStats.value = null
  activeDownloadStats.value = null
  activeSender.value?.cancel()
  activeSender.value = null
  receiverInstance.value = null
  error.value = ''
}

function onCameraError(e: QrScannerError) {
  // Append, don't replace: when a broken offer link opened the camera, its
  // explanation must survive a denied camera permission.
  const camera = `${e.message} You can paste the text instead.`
  error.value = error.value ? `${error.value} ${camera}` : camera
}

function onQrError(e: Error) {
  error.value = `QR could not be generated (${e.message}). Use the copy button instead.`
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const indicatorStatus = computed(() => (status.value === 'connected' ? 'online' : status.value === 'connecting' ? 'warning' : status.value === 'error' ? 'error' : 'offline'))
const statusText = computed(() => {
  if (status.value === 'connected') return 'Connected — data channel open'
  if (status.value === 'connecting') return role.value === 'host' && !answerApplied.value ? 'Waiting for the answer from the other device' : 'Connecting…'
  if (status.value === 'error') return 'Connection error'
  return 'Disconnected'
})

// Opened from the host's QR by the camera app: `#o=<offer>`.
function consumeOfferHash() {
  const hash = window.location.hash
  if (!hash.startsWith('#o=')) return
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
  const token = offerTokenFromHash(hash)
  if (connectionId.value !== null) {
    error.value = 'A session is already open on this page. Disconnect it first, then open the link again.'
    return
  }
  role.value = 'guest'
  if (token && isOffer(token)) {
    offerInput.value = token // the watcher accepts it
  }
  else {
    showScanner.value = true
    error.value = 'The link did not contain a complete offer. Scan the QR on the other device again.'
  }
}

onMounted(() => {
  window.addEventListener('webrtc-message', onMessage)
  window.addEventListener('webrtc-binary', onBinaryMessage)
  window.addEventListener('webrtc-state', onState)

  consumeOfferHash()
  // The camera app may hand the link to a tab that already shows this page;
  // then only the fragment changes and onMounted does not run again.
  window.addEventListener('hashchange', consumeOfferHash)
})

onUnmounted(() => {
  window.removeEventListener('hashchange', consumeOfferHash)
  window.removeEventListener('webrtc-binary', onBinaryMessage)
  window.removeEventListener('webrtc-message', onMessage)
  window.removeEventListener('webrtc-state', onState)
  for (const file of receivedFiles.value) {
    if (file.url) URL.revokeObjectURL(file.url)
  }
  if (connectionId.value) webrtcManager.closeConnection(connectionId.value)
})
</script>
